/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavbarLangOnly from "../../components/Home/navbarLang/NavbarLangOnly";
import { ToastContainer } from "react-toastify";
import { useAuth } from "../../context/auth/AuthContext";
import PaymentSuccessUI from "./PaymentSuccessUI";
import { saveTenantPlan } from "../utils/saveTenantPlan";
import { getSubscriptionPaymentStatus } from "../../Stores/utils/storeApi";

export default function PaymentSuccess() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(search);
  const orderId = params.get("orderId") || params.get("OrderId") || "";
  const isUpgrade =
    params.get("upgrade") === "true" ||
    localStorage.getItem("pendingPaymentUpgrade") === "true";
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const { login: authLogin } = useAuth();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const COLORS = {
    darkPurple: "#21003D",
    purple: "#7400BF",
    gold: "#F2B441",
    gradient: "linear-gradient(135deg, #21003D, #7400BF)",
    textLight: "#f3e9ff",
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    let user = {};
    try {
      user = JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      user = {};
    }

    const permissions = {
      viewOrders: 1, createOrder: 1, editOrder: 1, deleteOrder: 1,
      viewProducts: 1, createProduct: 1, editProduct: 1, deleteProduct: 1,
      viewStock: 1, viewDivisions: 1, addDivisions: 1, editDivisions: 1, deleteDivisions: 1,
      viewCategories: 1, addCategories: 1, editCategories: 1, deleteCategories: 1,
      viewCustomers: 1, addCustomers: 1, editCustomers: 1, deleteCustomers: 1,
      viewBanners: 1, addBanners: 1, editBanners: 1, deleteBanners: 1,
      viewTheme: 1, editTheme: 1,
      viewStoreSettings: 1, editStoreSettings: 1,
      viewCoupons: 1, addCoupons: 1, editCoupons: 1, deleteCoupons: 1,
      viewActivity: 1, viewSubscriptions: 1, editSubscriptions: 1,
      viewNotification: 1, addNotification: 1,
      viewPaymentMethods: 1, addPaymentMethods: 1, editPaymentMethods: 1, deletePaymentMethods: 1,
      viewShipping: 1, addShipping: 1, editShipping: 1, deleteShipping: 1,
      viewInvoice: 1, editInvoice: 1,
      viewAnalytics: 1, viewSalesReport: 1, viewInventoryReport: 1,
      viewCustomerReport: 1, viewProductReport: 1,
      viewEmployees: 1, addEmployees: 1, editEmployees: 1, deleteEmployees: 1,
    };

    authLogin({
      token,
      user,
      authRole: "tenant",
      isTenantOwner: true,
      permissions,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const persistPaidPlan = async (payload) => {
      const planId = parseInt(
        payload?.planId ||
          (isUpgrade
            ? localStorage.getItem("upgradePlanId")
            : localStorage.getItem("selectedPlanId")) ||
          "0",
        10
      );
      if (!planId) return;

      if (isUpgrade) {
        for (let i = 1; i <= 8; i += 1) {
          const note = localStorage.getItem(`upgradePlanNote${i}`) || "";
          if (note) localStorage.setItem(`selectedPlanNote${i}`, note);
        }
      }

      const plan = {
        planId,
        title:
          (isUpgrade
            ? localStorage.getItem("upgradePlanType")
            : localStorage.getItem("selectedPlanType")) || "",
        engTitle:
          (isUpgrade
            ? localStorage.getItem("upgradeEngPlanType")
            : localStorage.getItem("selectedEngPlanType")) || "",
        price: parseFloat(
          (isUpgrade
            ? localStorage.getItem("upgradePlanPrice")
            : localStorage.getItem("selectedPlanPrice")) || "0"
        ),
        desc:
          (isUpgrade
            ? localStorage.getItem("upgradePlanDescription")
            : localStorage.getItem("selectedPlanDesc")) || "",
      };

      await saveTenantPlan(plan, isArabic);
      localStorage.setItem("paymentStatus", "success");
      localStorage.removeItem("pendingPaymentUpgrade");
      sessionStorage.removeItem("trialExpiredAlertShown");
      sessionStorage.removeItem("paidExpiredAlertShown");
    };

    const checkPaymentStatus = async () => {
      if (!orderId) {
        setPaymentStatus(false);
        setLoading(false);
        return;
      }

      localStorage.setItem("lastPaymentOrderId", orderId);

      let result = await getSubscriptionPaymentStatus(orderId);

      for (let i = 0; i < 3 && result?.isPending; i += 1) {
        await wait(1500);
        if (cancelled) return;
        result = await getSubscriptionPaymentStatus(orderId);
      }

      if (cancelled) return;

      const payload = result?.data || null;
      const confirmedPaid = Boolean(result?.success) && Boolean(result?.isPaid);

      setPaymentStatus(confirmedPaid);
      setLoading(false);

      if (confirmedPaid) {
        try {
          await persistPaidPlan(payload);
        } catch (err) {
          console.error("Failed to save tenant plan after payment:", err);
        }
      }
    };

    checkPaymentStatus();
    return () => {
      cancelled = true;
    };
  }, [orderId, isArabic, isUpgrade]);

  const isSuccess = paymentStatus === true;

  const goNext = () => {
    const hasToken = Boolean(localStorage.getItem("authToken"));

    if (isSuccess) {
      if (!hasToken) {
        navigate("/login");
        return;
      }
      navigate(isUpgrade ? "/admin-store/subscriptions" : "/admin-store");
      return;
    }

    navigate(isUpgrade ? "/admin-store/subscriptions" : "/payment");
  };

  useEffect(() => {
    if (paymentStatus === null) return;

    const timer = setTimeout(goNext, 3000);
    return () => clearTimeout(timer);
  }, [isSuccess, paymentStatus, navigate, isUpgrade]);

  const handleRedirect = () => {
    goNext();
  };

  return (
    <>
      <NavbarLangOnly />
      <ToastContainer />
      <PaymentSuccessUI
        isArabic={isArabic}
        isMobile={isMobile}
        loading={loading}
        isSuccess={isSuccess}
        COLORS={COLORS}
        handleRedirect={handleRedirect}
      />
    </>
  );
}
